import React, { Suspense, useCallback, useEffect } from 'react';
import { BrowserRouter as Router, Routes, Route, Navigate, useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { jwtDecode } from 'jwt-decode';
import { Login } from './pages/Login';
import { Register } from './pages/Register';
import { ProtectedRoute } from './components/ProtectedRoute';
import { AppGuideProvider } from './components/AppGuide';
import { UploadProgressProvider } from './context/UploadProgressContext';
import { ReuseLibraryProvider } from './context/ReuseLibraryContext';
import { authService } from './services/auth.service.optimized';
import DesktopPreferences from './components/desktop-preferences';

// Lazy load heavy components for better performance
const Dashboard = React.lazy(() => import('./pages/Dashboard').then(module => ({ default: module.Dashboard })));
const SuperAdminDashboard = React.lazy(() => import('./pages/SuperAdminDashboard').then(module => ({ default: module.SuperAdminDashboard })));
const CompanyDashboard = React.lazy(() => import('./pages/CompanyDashboard').then(module => ({ default: module.CompanyDashboard })));
const Testing = React.lazy(() => import('./pages/Testing').then(module => ({ default: module.Testing })));
const PerformanceDashboard = React.lazy(() => import('./pages/PerformanceDashboard').then(module => ({ default: module.PerformanceDashboard })));
const ChatInterface = React.lazy(() => import('./pages/ChatInterface').then(module => ({ default: module.ChatInterface })));
const SystemSettings = React.lazy(() => import('./pages/SystemSettings').then(module => ({ default: module.SystemSettings })));
const McpOAuthCallback = React.lazy(() => import('./pages/McpOAuthCallback').then(module => ({ default: module.McpOAuthCallback })));
const Help = React.lazy(() => import('./pages/Help').then(module => ({ default: module.Help })));

interface DecodedToken {
    role: string;
}

// Helper function to determine the default route based on role
const getDefaultRoute = () => {
    const token = authService.getToken();
    if (!token) return '/login';
    try {
        const decoded = jwtDecode<DecodedToken>(token);
        return decoded.role === 'superadmin' ? '/superadmin/dashboard' : '/dashboard';
    } catch (e) {
        return '/login';
    }
};

const PluginsMcpRedirect: React.FC = () => {
    const location = useLocation();
    const role = authService.decodeToken()?.role;
    const pathname = role === 'superadmin' ? '/superadmin/dashboard' : '/dashboard';

    return (
        <Navigate
            to={{ pathname, search: location.search }}
            replace
            state={{ pluginsSection: 'mcp' }}
        />
    );
};

const LoadingFallback: React.FC = () => (
    <div className="flex min-h-screen items-center justify-center bg-background">
        <div className="h-10 w-10 animate-spin rounded-full border-2 border-primary border-t-transparent" />
    </div>
);

const App: React.FC = () => {
    const { i18n } = useTranslation();
    const [languageLoaded, setLanguageLoaded] = React.useState(false);
    const [currentLanguage, setCurrentLanguage] = React.useState<string>('');

    const applyLanguage = useCallback(async (language: string) => {
        if (language !== 'zh' && language !== 'en') return false;

        if (language !== i18n.language) {
            await i18n.changeLanguage(language);
        }

        localStorage.setItem('userLanguage', language);
        setCurrentLanguage(language);
        return true;
    }, [i18n]);

    // Database language is the source of truth after authentication. Custom events let
    // settings/login updates refresh the router language key without a page reload.
    const loadLanguagePreference = useCallback(async (preferredLanguage?: string) => {
        try {
            if (preferredLanguage && await applyLanguage(preferredLanguage)) {
                return;
            }

            const token = authService.getToken();
            if (!token) {
                setCurrentLanguage(i18n.language);
                return;
            }

            if (!authService.isTokenValid()) {
                authService.logout();
                setCurrentLanguage(i18n.language);
                return;
            }

            const dbLanguage = await authService.fetchLanguageFromDatabase();
            if (dbLanguage && await applyLanguage(dbLanguage)) {
                return;
            }

            setCurrentLanguage(i18n.language);
        } catch (error) {
            console.error('[App] Error loading language setting:', error);
            try {
                const savedLanguage = localStorage.getItem('userLanguage');
                if (savedLanguage && await applyLanguage(savedLanguage)) {
                    return;
                }
            } catch (fallbackError) {
                console.error('[App] Error applying fallback language:', fallbackError);
            }

            setCurrentLanguage(i18n.language);
        } finally {
            setLanguageLoaded(true);
        }
    }, [applyLanguage, i18n]);

    useEffect(() => {
        loadLanguagePreference();
    }, [loadLanguagePreference]);

    useEffect(() => {
        const refreshLanguage = (event: Event) => {
            const preferredLanguage = (event as CustomEvent<{ language?: string }>).detail?.language;
            loadLanguagePreference(preferredLanguage);
        };

        window.addEventListener('authStateChanged', refreshLanguage);
        window.addEventListener('languagePreferenceChanged', refreshLanguage);

        return () => {
            window.removeEventListener('authStateChanged', refreshLanguage);
            window.removeEventListener('languagePreferenceChanged', refreshLanguage);
        };
    }, [loadLanguagePreference]);

    // Show loading screen until language is loaded
    if (!languageLoaded) {
        console.log('[App] 🔄 Waiting for language to load...');
        return <><LoadingFallback /><DesktopPreferences /></>;
    }

    // Log the final language state before rendering
    console.log('[App] ✅ Language loaded, rendering app with language:', i18n.language);
    console.log('[App] 🔑 Current language state:', currentLanguage);
    console.log('[App] 📦 localStorage userLanguage:', localStorage.getItem('userLanguage'));

    return (
        <>
        <DesktopPreferences />
        <Suspense fallback={<LoadingFallback />}>
            <UploadProgressProvider>
                <ReuseLibraryProvider>
                    <Router future={{ v7_startTransition: true }}>
                        <AppGuideProvider>
                        <Routes>
                            <Route path="/login" element={<Login />} />
                            <Route path="/register" element={<Register />} />
                            <Route path="/mcp/oauth/callback" element={<McpOAuthCallback />} />
                            <Route path="/api/v1/mcp/oauth/callback" element={<McpOAuthCallback />} />
                            
                            {/* Regular User Dashboard */}
                            <Route
                                path="/dashboard"
                                element={
                                    <ProtectedRoute allowedRoles={['admin', 'member']}>
                                        <Dashboard />
                                    </ProtectedRoute>
                                }
                            />
                            
                            {/* Super Admin Dashboard */}
                            <Route
                                path="/superadmin/dashboard"
                                element={
                                    <ProtectedRoute allowedRoles={['superadmin']}>
                                        <SuperAdminDashboard />
                                    </ProtectedRoute>
                                }
                            />
                            
                            {/* Company Dashboard */}
                            <Route
                                path="/company/dashboard"
                                element={
                                    <ProtectedRoute allowedRoles={['admin', 'member']}>
                                        <CompanyDashboard />
                                    </ProtectedRoute>
                                }
                            />
                            
                            {/* Testing Page */}
                            <Route
                                path="/testing"
                                element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'member']}>
                                        <Testing />
                                    </ProtectedRoute>
                                }
                            />
                            
                            {/* Performance Dashboard */}
                            <Route
                                path="/performance/dashboard"
                                element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'member']}>
                                        <PerformanceDashboard />
                                    </ProtectedRoute>
                                }
                            />
                            
                            {/* Chat Interface */}
                            <Route
                                path="/chat"
                                element={
                                    <ProtectedRoute allowedRoles={['admin', 'member']}>
                                        <ChatInterface />
                                    </ProtectedRoute>
                                }
                            />
                            
                            {/* System Settings */}
                            <Route
                                path="/system/settings"
                                element={
                                    <ProtectedRoute allowedRoles={['superadmin']}>
                                        <SystemSettings />
                                    </ProtectedRoute>
                                }
                            />

                            {/* MCP bookmarks and OAuth returns open Plugins on the MCP section. */}
                            <Route
                                path="/mcp/settings"
                                element={
                                    <ProtectedRoute allowedRoles={['superadmin', 'admin', 'member']}>
                                        <PluginsMcpRedirect />
                                    </ProtectedRoute>
                                }
                            />
                            
                            {/* Help Page */}
                            <Route path="/help" element={<Help />} />
                            
                            {/* Default route redirects based on role */}
                            <Route path="/" element={<Navigate to={getDefaultRoute()} replace />} />
                        </Routes>
                    </AppGuideProvider>
                </Router>
                </ReuseLibraryProvider>
            </UploadProgressProvider>
        </Suspense>
        </>
    );
};

export default App; 
